import { Code, ConnectError } from '@connectrpc/connect'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingBlockBidStatus } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'
import type { BidWithdrawState } from '~/pages/SpotBidding/models/bidWithdrawState'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import { translateBlockWriteError } from '~/pages/SpotBidding/models/blockBidWrite'
import { formatBlockVolumeLabel } from '~/pages/SpotBidding/models/blockVolumeLabel'

/**
 * ブロック入札グループの行に「取下」を出すか (AC-BSP-057 / AC-BSP-058)。 取下を受け付けるかは
 * backend の応答 (withdrawable) で決まる。 画面は、 応答の後に入札締切を跨いだ場合と、
 * 確定済ワークフロー経由の読み取り専用 (backend に概念が無い) だけを重ねて見る。
 */
export const canWithdrawBlockBidGroup = ({
  group,
  isBeforeDeadline,
  isWorkflowReadonly,
}: {
  group: Pick<BlockBidRow, 'withdrawable'>
  isBeforeDeadline: boolean
  isWorkflowReadonly: boolean
}): boolean => group.withdrawable && isBeforeDeadline && !isWorkflowReadonly

/** 全 leg が同じ取下の状態にある時だけ、その状態を返す。 */
export const resolveBlockBidGroupWithdrawState = (
  legBidStatuses: readonly BiddingBlockBidStatus[],
): BidWithdrawState | undefined => {
  const allAre = (status: BiddingBlockBidStatus): boolean =>
    legBidStatuses.length > 0 && legBidStatuses.every((s) => s === status)
  if (allAre(BiddingBlockBidStatus.WITHDRAWING)) return 'withdrawing'
  if (allAre(BiddingBlockBidStatus.WITHDRAW_FAILED)) return 'withdrawFailed'
  return undefined
}

/**
 * 取下 RPC の失敗を画面の文言にする。 取下の FailedPrecondition は (締切の拒否が理由を
 * 持っていても) 理由別の編集・削除向け文言でなく取下の文言にする。 それ以外は書込共通の扱い。
 */
export const translateBlockWithdrawError = (
  err: unknown,
  t: TFunction,
): string =>
  err instanceof ConnectError && err.code === Code.FailedPrecondition
    ? t('page:biddingBlock.serverError.withdrawFailedPrecondition')
    : translateBlockWriteError(err, t)

/** leg 1 本のサマリ (名称 / エリア / 売買 / コマ範囲 / 単価 × 数量)。 */
export const formatBlockBidLegSummary = (
  leg: BlockBidRow,
  t: TFunction,
): string =>
  t('page:biddingBlock.withdrawConfirm.leg', {
    name: t(leg.nameKey),
    area: convertAreaLabel(leg.areaCode),
    side:
      leg.direction === Side.BUY
        ? t('common:tradeSide.buy')
        : t('common:tradeSide.sell'),
    komaRange: leg.komaRangeLabel,
    price: leg.priceYenPerKwh.toFixed(2),
    volume: formatBlockVolumeLabel(leg.slotVolumes, leg.volumeMwh),
  })

/** 取下確認モーダルに出すグループのサマリ (1 行 1 要素)。 */
export const formatBlockBidGroupWithdrawSummary = (
  legs: readonly BlockBidRow[],
  t: TFunction,
): string[] => [
  t('page:biddingBlock.withdrawConfirm.lead'),
  ...legs.map((leg) => formatBlockBidLegSummary(leg, t)),
  ...(legs.length > 1 ? [t('page:biddingBlock.withdrawConfirm.pairNote')] : []),
]

/**
 * 下書きのブロック入札の 1 件削除確認に出す、 グループの leg のサマリ (1 行 1 要素。 AC-BSP-139)。
 * ブロックにはコードも名称も無いため、 leg ごとの属性で削除対象を示す。
 */
export const formatBlockBidGroupDeleteSummary = (
  legs: readonly BlockBidRow[],
  t: TFunction,
): string[] => [
  ...legs.map((leg) => formatBlockBidLegSummary(leg, t)),
  ...(legs.length > 1 ? [t('page:biddingBlock.deleteModal.pairNote')] : []),
]
